/* eslint-disable react/function-component-definition */
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { parseDate } from "../../../helpers/date";
import {
  useGetAllBaseRateFilesQuery,
  useGetBaseRateFileTermsQuery,
  useUploadBaseRateFileMutation,
  useUpdateBaseRateFileMutation,
  useDeleteBaseRateFileMutation,
  useGetSourceStatusQuery,
} from "../../../store/queries/baseRateFiles";
import {
  useGetContractFeeSchedulesQuery,
  type IContractFeeSchedule,
  type IContractTermInGroup,
} from "../../../store/queries/contractFeeSchedules";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import { IBaseRateFile } from "../../../types/baseRateFile";
import CreateBaseRateFileDialog from "./CreateBaseRateFileDialog";
import UploadSourceFilesDialog from "./UpoadSourceFilesDialog/UploadSourceFilesDialog";

/**
 * Date-only values for PrimeReact Calendar: local noon avoids UTC day-boundary bugs
 * (e.g. May 1 showing as April 30 when the component normalizes to UTC).
 */
function localDateForCalendar(
  y: number,
  monthIndex: number,
  day: number
): Date {
  return new Date(y, monthIndex, day, 12, 0, 0, 0);
}

/** Parse yyyy-mm-dd as a local calendar date for display / Calendar value. */
function parseIsoDateOnlyLocal(raw: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw).trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || mo < 0 || mo > 11 || d < 1 || d > 31) {
    return null;
  }
  const dt = localDateForCalendar(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
    return null;
  }
  return dt;
}

function termEffectiveSortKey(t: IBaseRateFile): number {
  const raw = t.effectiveDate;
  if (raw != null && raw !== "") {
    const parsed = parseIsoDateOnlyLocal(String(raw));
    if (parsed) return parsed.getTime();
  }
  if (t.effectiveYear != null) {
    return new Date(t.effectiveYear, 0, 1).getTime();
  }
  return 0;
}

function formatBaseRateTermRowDate(t: IBaseRateFile): string {
  const raw = t.effectiveDate;
  if (raw != null && raw !== "") {
    const local = parseIsoDateOnlyLocal(String(raw));
    if (local) return local.toLocaleDateString();
  }
  if (t.effectiveYear != null) {
    return String(t.effectiveYear);
  }
  return "Date unknown";
}

/** YYYY-MM-DD for an existing term row (duplicate new-term date check vs DB unique key). */
function existingTermEffectiveDateIso(t: IBaseRateFile): string | undefined {
  if (t.effectiveDate != null && String(t.effectiveDate).trim() !== "") {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(t.effectiveDate).trim());
    if (m) return m[1];
  }
  if (t.effectiveYear != null) {
    return `${t.effectiveYear}-01-01`;
  }
  return undefined;
}

function isoDateYmdPrefix(raw: string | null | undefined): string | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw).trim());
  return m ? m[1] : undefined;
}

/** Trimmed label; uniqueness mirrors API (per account list). */
function baseRateDisplayNameLabel(raw: string): string {
  return raw.trim();
}

/**
 * Row using the same display name as another logical file (different `fileName`).
 * Exclude rows for `excludeLogicalFileName` so terms of the same file can share a label.
 */
function baseRateDisplayNameColumnBody(rowData: IBaseRateFile) {
  return (
    <div className="flex flex-column align-items-start gap-1 w-full min-w-0 max-w-full">
      <span
        className="w-full overflow-hidden text-overflow-ellipsis white-space-nowrap"
        title={rowData.displayName}
      >
        {rowData.displayName}
      </span>
      {rowData.isCarveout ? (
        <Tag
          value="Carveout"
          severity="warning"
          rounded
          className="text-xs font-semibold"
        />
      ) : null}
    </div>
  );
}

function findConflictingBaseRateByDisplayName(
  rows: IBaseRateFile[],
  inputDisplayName: string,
  excludeLogicalFileName: string | undefined,
  isCarveout?: boolean
): IBaseRateFile | undefined {
  const label = baseRateDisplayNameLabel(inputDisplayName);
  if (!label) return undefined;
  const wantCarveout = isCarveout === true;
  return rows.find((f) => {
    if (
      excludeLogicalFileName != null &&
      f.fileName === excludeLogicalFileName
    ) {
      return false;
    }
    if ((f.isCarveout === true) !== wantCarveout) {
      return false;
    }
    return baseRateDisplayNameLabel(f.displayName) === label;
  });
}

function contractReferencesAnyBaseRateTermId(
  contract: IContractFeeSchedule,
  baseTermIds: Set<string>
): boolean {
  return (
    contract.rateCalculations?.some(
      (rc) =>
        rc.baseRateFileId != null &&
        rc.baseRateFileId !== "" &&
        baseTermIds.has(rc.baseRateFileId)
    ) ?? false
  );
}

function contractReferencesCarveoutBaseRateTermId(
  contract: IContractFeeSchedule,
  carveoutTermIds: Set<string>
): boolean {
  const fromDetail = contract.carveOutFiles ?? [];
  const fromList = contract.carveouts ?? [];
  const links = [...fromDetail, ...fromList];
  const seen = new Set<string>();
  return links.some((c) => {
    const bid = c.baseRateFileId;
    if (bid == null || String(bid).trim() === "") return false;
    const key = `${c.id}:${bid}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return carveoutTermIds.has(bid);
  });
}

/** Effective dates (YYYY-MM-DD) already used by this logical file or any contract row referencing its term ids. */
function collectOccupiedNewTermEffectiveDates(
  fileTerms: IBaseRateFile[],
  selected: IBaseRateFile | null,
  contracts: IContractFeeSchedule[],
  baseIds: Set<string>
): Set<string> {
  const s = new Set<string>();
  fileTerms.forEach((t) => {
    const iso = existingTermEffectiveDateIso(t);
    if (iso) s.add(iso);
  });
  if (selected) {
    const iso = existingTermEffectiveDateIso(selected);
    if (iso) s.add(iso);
  }
  contracts.forEach((c) => {
    if (!contractReferencesAnyBaseRateTermId(c, baseIds)) return;
    const iso = isoDateYmdPrefix(c.effectiveDate);
    if (iso) s.add(iso);
  });
  return s;
}

/** Normalize Calendar onChange values to a stable local calendar day (noon). */
function calendarSelectionToLocalDay(
  value: Date | null | undefined
): Date | null {
  if (
    value == null ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  ) {
    return null;
  }
  return localDateForCalendar(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  );
}

const StyledDialog = styled(Dialog)`
  .selected-files-list {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;

    .selected-file-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #f8f9fa;

      &:last-child {
        border-bottom: none;
      }

      .file-name {
        flex: 1;
        margin-right: 1rem;
      }

      .file-size {
        color: #6c757d;
        margin-right: 1rem;
        font-size: 0.875rem;
      }

      .remove-file-btn {
        background: transparent;
        border: none;
        color: #6c757d;
        width: 2rem;
        height: 2rem;
        padding: 0;

        &:hover {
          background: transparent;
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        .p-button-icon {
          font-size: 1rem;
        }
      }
    }
  }

  .file-upload-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }
`;

interface IBaseRateFilesTableProps {
  accountId?: string;
}

function toIsoDateOnly(value: Date | null): string | undefined {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return undefined;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayForCalendar(): Date {
  const n = new Date();
  return localDateForCalendar(n.getFullYear(), n.getMonth(), n.getDate());
}

const VALIDATION_PREFIX = "Base rate file validation failed:";

function parseBaseRateValidationErrors(
  message: string | undefined
): string[] | null {
  if (!message || !message.startsWith(VALIDATION_PREFIX)) return null;
  return message
    .slice(VALIDATION_PREFIX.length)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

const BaseRateFilesTable: React.FC<IBaseRateFilesTableProps> = ({
  accountId,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [updateDialogVisible, setUpdateDialogVisible] = useState(false);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [uploadSourceFilesDialogVisible, setUploadSourceFilesDialogVisible] =
    useState(false);
  const [selectedFileForUpdate, setSelectedFileForUpdate] =
    useState<IBaseRateFile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [uploadEffectiveDate, setUploadEffectiveDate] = useState<Date | null>(
    () => todayForCalendar()
  );
  const [uploadIsCarveout, setUploadIsCarveout] = useState(false);

  const [updateMode, setUpdateMode] = useState<"same" | "newTerm">("same");
  const [newTermEffectiveDate, setNewTermEffectiveDate] = useState<Date | null>(
    () => todayForCalendar()
  );
  const [updateActivateContracts, setUpdateActivateContracts] = useState(false);
  /** Base rate row id to PUT when updating the same term (may be an older term than the list row opened from). */
  const [updateTargetTermId, setUpdateTargetTermId] = useState<string | null>(
    null
  );

  const { data: baseRateFilesData, isLoading } = useGetAllBaseRateFilesQuery({
    search: searchValue,
    accountId,
  });
  const {
    data: sourceStatusData,
    isLoading: isSourceStatusLoading,
    refetch: refetchSourceStatus,
  } = useGetSourceStatusQuery();
  const [uploadFile] = useUploadBaseRateFileMutation();
  const [updateFile] = useUpdateBaseRateFileMutation();
  const [deleteFile] = useDeleteBaseRateFileMutation();

  const { data: fileTerms = [], isFetching: termsLoading } =
    useGetBaseRateFileTermsQuery(
      {
        fileName: selectedFileForUpdate?.fileName ?? "",
        accountId,
      },
      {
        skip: !updateDialogVisible || !selectedFileForUpdate?.fileName,
      }
    );

  const {
    data: contractsForRolloutScan = [],
    isFetching: contractsScanLoading,
  } = useGetContractFeeSchedulesQuery(
    { accountId, groupByContractGroup: false },
    { skip: !updateDialogVisible }
  );

  const baseRateTermIdsForUpdate = useMemo(() => {
    const ids = new Set<string>();
    if (selectedFileForUpdate?.id) ids.add(selectedFileForUpdate.id);
    fileTerms.forEach((t) => ids.add(t.id));
    return ids;
  }, [selectedFileForUpdate?.id, fileTerms]);

  const occupiedNewTermEffectiveDates = useMemo(() => {
    if (!updateDialogVisible || updateMode !== "newTerm") {
      return new Set<string>();
    }
    return collectOccupiedNewTermEffectiveDates(
      fileTerms,
      selectedFileForUpdate,
      contractsForRolloutScan,
      baseRateTermIdsForUpdate
    );
  }, [
    updateDialogVisible,
    updateMode,
    fileTerms,
    selectedFileForUpdate,
    contractsForRolloutScan,
    baseRateTermIdsForUpdate,
  ]);

  const isBaseRateFileUsedInContracts = useMemo(() => {
    if (baseRateTermIdsForUpdate.size === 0) return false;
    return contractsForRolloutScan.some((contract) =>
      contractReferencesAnyBaseRateTermId(contract, baseRateTermIdsForUpdate)
    );
  }, [contractsForRolloutScan, baseRateTermIdsForUpdate]);

  /** Contracts whose rate lines reference any term row of this logical base rate file (same scan as rollout). */
  const contractsUsingThisBaseRate = useMemo(() => {
    if (!updateDialogVisible || baseRateTermIdsForUpdate.size === 0) return [];
    const matched = contractsForRolloutScan.filter((c) =>
      contractReferencesAnyBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
    return [...matched].sort((a, b) => {
      const aProv = a.provider?.name?.toString() || "";
      const bProv = b.provider?.name?.toString() || "";
      const provCmp = aProv.localeCompare(bProv, undefined, {
        sensitivity: "base",
      });
      if (provCmp !== 0) return provCmp;
      const aPayer = a.payer?.name?.toString() || "";
      const bPayer = b.payer?.name?.toString() || "";
      return aPayer.localeCompare(bPayer, undefined, { sensitivity: "base" });
    });
  }, [updateDialogVisible, contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const contractsUsingThisCarveout = useMemo(() => {
    if (!updateDialogVisible || baseRateTermIdsForUpdate.size === 0) return [];
    const matched = contractsForRolloutScan.filter((c) =>
      contractReferencesCarveoutBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
    return [...matched].sort((a, b) => {
      const aProv = a.provider?.name?.toString() || "";
      const bProv = b.provider?.name?.toString() || "";
      const provCmp = aProv.localeCompare(bProv, undefined, {
        sensitivity: "base",
      });
      if (provCmp !== 0) return provCmp;
      const aPayer = a.payer?.name?.toString() || "";
      const bPayer = b.payer?.name?.toString() || "";
      return aPayer.localeCompare(bPayer, undefined, { sensitivity: "base" });
    });
  }, [updateDialogVisible, contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const isCarveoutReferencedInContracts = useMemo(() => {
    if (baseRateTermIdsForUpdate.size === 0) return false;
    return contractsForRolloutScan.some((c) =>
      contractReferencesCarveoutBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
  }, [contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const isCarveoutUpdateFile = selectedFileForUpdate?.isCarveout === true;

  const contractDetailHref = (contractId: string) =>
    accountId
      ? `/hospitals/view/${accountId}/contracts/${contractId}`
      : `/payer-rules/contracts/${contractId}`;

  const updateRolloutScanReady =
    updateDialogVisible && !termsLoading && !contractsScanLoading;

  const showRolloutDraftOptions =
    updateMode === "newTerm" &&
    updateRolloutScanReady &&
    isBaseRateFileUsedInContracts &&
    !isCarveoutUpdateFile;

  useEffect(() => {
    if (
      updateDialogVisible &&
      isCarveoutUpdateFile &&
      updateMode === "newTerm"
    ) {
      setUpdateMode("same");
    }
  }, [updateDialogVisible, isCarveoutUpdateFile, updateMode]);

  useEffect(() => {
    if (!showRolloutDraftOptions && updateActivateContracts) {
      setUpdateActivateContracts(false);
    }
  }, [showRolloutDraftOptions, updateActivateContracts]);

  useEffect(() => {
    if (!updateDialogVisible || updateMode !== "same") return;
    if (fileTerms.length === 0) return;
    setUpdateTargetTermId((prev) => {
      if (prev && fileTerms.some((t) => t.id === prev)) return prev;
      const fromOpen = selectedFileForUpdate?.id;
      if (fromOpen && fileTerms.some((t) => t.id === fromOpen)) {
        return fromOpen;
      }
      const sorted = [...fileTerms].sort(
        (a, b) => termEffectiveSortKey(b) - termEffectiveSortKey(a)
      );
      return sorted[0]?.id ?? prev;
    });
  }, [updateDialogVisible, updateMode, fileTerms, selectedFileForUpdate?.id]);

  const fileTermsSortedForPicker = useMemo(
    () =>
      [...fileTerms].sort(
        (a, b) => termEffectiveSortKey(b) - termEffectiveSortKey(a)
      ),
    [fileTerms]
  );

  const updateContextRow = useMemo(() => {
    if (!selectedFileForUpdate) return null;
    if (updateMode !== "same" || !updateTargetTermId) {
      return selectedFileForUpdate;
    }
    return (
      fileTerms.find((t) => t.id === updateTargetTermId) ??
      selectedFileForUpdate
    );
  }, [selectedFileForUpdate, updateMode, updateTargetTermId, fileTerms]);

  const newTermEffectiveDateClash = useMemo(() => {
    if (updateMode !== "newTerm" || !newTermEffectiveDate) {
      return false;
    }
    const iso = toIsoDateOnly(newTermEffectiveDate);
    if (!iso) return false;
    return occupiedNewTermEffectiveDates.has(iso);
  }, [updateMode, newTermEffectiveDate, occupiedNewTermEffectiveDates]);

  const baseRateFiles = baseRateFilesData?.data || [];

  const uploadFilenameClash = useMemo(() => {
    if (!selectedFile) return undefined;
    return baseRateFiles.find(
      (f) =>
        f.fileName === selectedFile.name &&
        (f.isCarveout === true) === uploadIsCarveout
    );
  }, [selectedFile, baseRateFiles, uploadIsCarveout]);

  const uploadDisplayNameConflict = useMemo(() => {
    if (!uploadDialogVisible) return undefined;
    return findConflictingBaseRateByDisplayName(
      baseRateFiles,
      displayName,
      undefined,
      uploadIsCarveout
    );
  }, [uploadDialogVisible, baseRateFiles, displayName, uploadIsCarveout]);

  const updateDisplayNameConflict = useMemo(() => {
    if (!updateDialogVisible) return undefined;
    return findConflictingBaseRateByDisplayName(
      baseRateFiles,
      displayName,
      selectedFileForUpdate?.fileName,
      isCarveoutUpdateFile
    );
  }, [
    updateDialogVisible,
    baseRateFiles,
    displayName,
    selectedFileForUpdate?.fileName,
    isCarveoutUpdateFile,
  ]);

  const openUpdateDialog = (file: IBaseRateFile) => {
    setSelectedFileForUpdate(file);
    setUpdateTargetTermId(file.id);
    setDisplayName(file.displayName);
    setUpdateMode("same");
    setNewTermEffectiveDate(todayForCalendar());
    setUpdateActivateContracts(false);
    setUpdateDialogVisible(true);
  };

  const handleUpdateModeChange = (mode: "same" | "newTerm") => {
    setUpdateMode(mode);
    if (mode === "newTerm") {
      setNewTermEffectiveDate(todayForCalendar());
    }
  };

  const handleUpload = async () => {
    if (!displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    if (!uploadEffectiveDate) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }
    const uploadIso = toIsoDateOnly(uploadEffectiveDate);
    if (!uploadIso) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }

    const existingByFileName = baseRateFiles.find(
      (f) =>
        f.fileName === selectedFile.name &&
        (f.isCarveout === true) === uploadIsCarveout
    );
    if (existingByFileName) {
      toast.current?.show({
        severity: "warn",
        summary: "Use Update instead",
        detail:
          "A file with this name already exists for this type (base rate vs carveout). Open that file and use Update instead of a fresh upload.",
      });
      setUploadDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setUploadEffectiveDate(todayForCalendar());
      setUploadIsCarveout(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      openUpdateDialog(existingByFileName);
      return;
    }

    const dupLabel = findConflictingBaseRateByDisplayName(
      baseRateFiles,
      displayName,
      undefined,
      uploadIsCarveout
    );
    if (dupLabel) {
      toast.current?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${dupLabel.fileName}). Choose a distinct label.`,
      });
      return;
    }

    setIsUploading(true);

    try {
      await uploadFile({
        file: selectedFile,
        displayName,
        accountId,
        effectiveDate: uploadIso,
        ...(uploadIsCarveout ? { isCarveout: true } : {}),
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully",
      });

      setUploadDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setUploadIsCarveout(false);
      setUploadEffectiveDate(todayForCalendar());
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      const fetchErr = error as {
        data?: { message?: string; messages?: string[] };
      };
      const errText = fetchErr.data?.message ?? fetchErr.data?.messages?.[0];
      const parsed = parseBaseRateValidationErrors(errText);
      if (parsed) {
        setValidationErrors(parsed);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errText ?? "Failed to upload files",
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedFileForUpdate || !displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    if (updateMode === "newTerm") {
      if (isCarveoutUpdateFile) {
        toast.current?.show({
          severity: "error",
          summary: "Not supported",
          detail:
            "Carveout files cannot create a new term. Use version update on the existing term only.",
        });
        return;
      }
      if (!newTermEffectiveDate) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Please select an effective date for the new base rate term.",
        });
        return;
      }
    }

    const applyRollout = showRolloutDraftOptions && updateActivateContracts;
    const createDraftContracts =
      showRolloutDraftOptions && !updateActivateContracts;

    const newTermIso = toIsoDateOnly(newTermEffectiveDate);

    if (
      updateMode === "newTerm" &&
      newTermIso &&
      occupiedNewTermEffectiveDates.has(newTermIso)
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Duplicate effective date",
        detail:
          "This effective date is already used by a base rate term for this file or by a contract that references this file. Choose a different day.",
      });
      return;
    }

    const dupUpdateLabel = findConflictingBaseRateByDisplayName(
      baseRateFiles,
      displayName,
      selectedFileForUpdate?.fileName,
      isCarveoutUpdateFile
    );
    if (dupUpdateLabel) {
      toast.current?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${dupUpdateLabel.fileName}). Choose a distinct label.`,
      });
      return;
    }

    const contractEffectiveIso = showRolloutDraftOptions
      ? newTermIso
      : undefined;

    if (showRolloutDraftOptions) {
      if (!contractEffectiveIso) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "The new term date is invalid. Pick a valid effective date.",
        });
        return;
      }
    }

    const updateRowId =
      updateMode === "same"
        ? updateTargetTermId ?? selectedFileForUpdate.id
        : selectedFileForUpdate.id;

    setIsUploading(true);

    try {
      await updateFile({
        id: updateRowId,
        file: selectedFile,
        displayName,
        accountId,
        ...(updateMode === "newTerm" && newTermEffectiveDate
          ? {
              isNewTerm: true,
              effectiveDate: newTermIso,
            }
          : {}),
        activateContracts: applyRollout,
        contractEffectiveDate: contractEffectiveIso,
        ...(createDraftContracts ? { createDraftContracts: true } : {}),
      }).unwrap();

      let updateSuccessDetail: string;
      if (updateMode === "newTerm") {
        if (applyRollout) {
          updateSuccessDetail =
            "New term created. Matching active contracts were rolled forward where applicable.";
        } else if (createDraftContracts) {
          updateSuccessDetail =
            "New term created. Draft contract term(s) were created for affected deals—activate when you are ready to generate procedures.";
        } else {
          updateSuccessDetail =
            "New term created. No contracts reference this logical file yet.";
        }
      } else if (isCarveoutUpdateFile) {
        if (!isCarveoutReferencedInContracts) {
          updateSuccessDetail =
            "Carveout file updated. No contracts attach this file yet.";
        } else {
          updateSuccessDetail =
            "Carveout file updated. Contracts using this file may show as outdated until you refresh procedures.";
        }
      } else if (!isBaseRateFileUsedInContracts) {
        updateSuccessDetail =
          "Base rate file updated. No contracts reference this file yet.";
      } else {
        updateSuccessDetail =
          "Base rate file updated. Contracts using this term may show as outdated until you refresh procedures.";
      }

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: updateSuccessDetail,
        life: 6000,
      });

      setUpdateDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setSelectedFileForUpdate(null);
      setUpdateMode("same");
      setUpdateTargetTermId(null);
      setUpdateActivateContracts(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      const d = (error as { data?: Record<string, unknown> })?.data as
        | {
            message?: string;
            messages?: string[];
          }
        | undefined;
      const errText = d?.message ?? d?.messages?.[0];
      const parsed = parseBaseRateValidationErrors(errText);
      if (parsed) {
        setValidationErrors(parsed);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errText ?? "Failed to update base rate file",
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (file: IBaseRateFile) => {
    confirmDialog({
      message: `Are you sure you want to delete ${file.fileName}?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(file.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "File deleted successfully",
          });
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete file",
          });
        }
      },
    });
  };

  const handleDownload = async (file: IBaseRateFile) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${apiUrl.endsWith("/") ? "" : "/"}base-rate-files/${
          file.id
        }/download-url`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to get download URL",
      });
    }
  };

  const actionBodyTemplate = (rowData: IBaseRateFile) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleDownload(rowData)}
          tooltip="Download"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openUpdateDialog(rowData)}
          tooltip="Update"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const uploadDialog = (
    <StyledDialog
      header="Upload Base Rate or Carveout File"
      visible={uploadDialogVisible}
      onHide={() => {
        if (!isUploading) {
          setUploadDialogVisible(false);
          setDisplayName("");
          setSelectedFile(null);
          setUploadIsCarveout(false);
          setUploadEffectiveDate(todayForCalendar());
          setValidationErrors([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      }}
      style={{ width: "600px" }}
      closable={!isUploading}
      closeOnEscape={!isUploading}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="displayName">Display Name *</label>
          <InputText
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter a name for these base rate files"
          />
          {uploadDisplayNameConflict && (
            <Message
              severity="error"
              className="w-full mt-2"
              text={`Display name must be unique per account among logical files. Already used by “${uploadDisplayNameConflict.displayName}” (${uploadDisplayNameConflict.fileName}).`}
            />
          )}
        </div>
        <div className="field">
          <label htmlFor="uploadEffectiveDate">Term effective date *</label>
          <Calendar
            id="uploadEffectiveDate"
            value={uploadEffectiveDate}
            onChange={(e) =>
              setUploadEffectiveDate(
                calendarSelectionToLocalDay(e.value as Date | null)
              )
            }
            showIcon
            className="w-full"
            dateFormat="mm-dd-yy"
          />
        </div>
        <div className="field flex align-items-center gap-2">
          <Checkbox
            inputId="uploadIsCarveout"
            checked={uploadIsCarveout}
            onChange={(e) => setUploadIsCarveout(!!e.checked)}
          />
          <label htmlFor="uploadIsCarveout" className="m-0 cursor-pointer">
            Carveout file
          </label>
        </div>
        <p className="text-600 text-sm line-height-3 m-0">
          {uploadIsCarveout ? (
            <>
              Carveout files are not used until you attach them under{" "}
              <strong>Carve-Outs</strong> on a contract. Carveouts do not create
              new contract terms, only version updates are available here.
            </>
          ) : (
            <>
              New files are not on any contract until you attach them in
              contract rate calculations. Use <strong>Update</strong> on this
              file later to roll contracts forward, once they reference it.
            </>
          )}
        </p>
        <div className="field">
          <label>Select CSV file *</label>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setSelectedFile(e.target.files[0]);
                setValidationErrors([]);
                e.target.value = ""; // Reset input so same file can be selected again
              }
            }}
            style={{ display: "none" }}
            id="file-upload-input"
          />
          <Button
            label="Choose File"
            icon="pi pi-plus"
            className="p-button-outlined mb-3"
            onClick={() =>
              document.getElementById("file-upload-input")?.click()
            }
          />
          {selectedFile && (
            <>
              <div className="selected-files-list">
                <div className="selected-file-item">
                  <span className="file-name">{selectedFile.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => {
                      setSelectedFile(null);
                    }}
                  />
                </div>
              </div>
              {uploadFilenameClash && (
                <div className="mt-2">
                  <Message
                    severity="warn"
                    className="w-full"
                    text="A base rate file with this file name already exists. Use Update on that file (new term or new version) instead of a fresh upload."
                  />
                  <Button
                    type="button"
                    label="Open update for this file"
                    icon="pi pi-pencil"
                    className="p-button-outlined mt-2"
                    onClick={() => {
                      const row = uploadFilenameClash;
                      setUploadDialogVisible(false);
                      setSelectedFile(null);
                      setDisplayName("");
                      setUploadEffectiveDate(todayForCalendar());
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                      openUpdateDialog(row);
                    }}
                  />
                </div>
              )}
              {validationErrors.length > 0 && (
                <div className="mt-3 p-3 border-round surface-100 border-1 border-red-300">
                  <div className="flex align-items-center gap-2 mb-2 text-red-700 font-semibold">
                    <i className="pi pi-exclamation-triangle" />
                    File validation failed
                  </div>
                  <ul className="m-0 pl-4 text-sm line-height-3">
                    {validationErrors.map((err, i) => (
                      <li key={i} className="mb-1 text-red-700">
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="file-upload-actions">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                />
                <Button
                  label="Upload"
                  icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                  onClick={handleUpload}
                  disabled={
                    isUploading ||
                    !!uploadFilenameClash ||
                    !!uploadDisplayNameConflict
                  }
                  loading={isUploading}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </StyledDialog>
  );

  return (
    <div>
      <Toast ref={toast} />
      <div className="mb-3 flex justify-content-between">
        <div className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search base rate files..."
          />
        </div>
        <div className="flex gap-2">
          <Button
            label="Upload Source Files"
            icon="pi pi-cloud-upload"
            className="p-button-outlined"
            loading={isSourceStatusLoading}
            onClick={() => setUploadSourceFilesDialogVisible(true)}
          />
          <Button
            label="Create File"
            icon="pi pi-plus"
            className="p-button-outlined"
            onClick={() => setCreateDialogVisible(true)}
          />
          <Button
            label="Upload Files"
            icon="pi pi-upload"
            onClick={() => setUploadDialogVisible(true)}
          />
        </div>
      </div>
      <DataTable
        value={baseRateFiles || []}
        loading={isLoading}
        paginator
        rows={10}
        emptyMessage="No files uploaded yet."
        className="p-datatable-striped"
      >
        <Column
          field="displayName"
          header="Display Name"
          sortable
          body={baseRateDisplayNameColumnBody}
          style={{ maxWidth: "18rem" }}
        />
        <Column field="fileName" header="File Name" sortable />
        <Column
          header="Terms"
          body={(row: IBaseRateFile) => {
            // eslint-disable-next-line no-underscore-dangle
            const n = row._termCount;
            if (n == null || n <= 0) return "—";
            return `${n} term${n === 1 ? "" : "s"}`;
          }}
          style={{ width: "100px" }}
        />
        <Column
          field="effectiveYear"
          header="Year"
          sortable
          body={(row: IBaseRateFile) =>
            row.effectiveYear != null ? String(row.effectiveYear) : "—"
          }
          style={{ width: "90px" }}
        />
        <Column
          field="uploadedBy.name"
          header="Uploaded By"
          body={(rowData) => rowData.uploadedBy?.name}
        />
        <Column
          field="createdAt"
          header="Date Uploaded"
          sortable
          body={(rowData) => parseDate(rowData.createdAt)}
        />
        <Column
          header="Procedures"
          body={(rowData) => {
            const rowCount = rowData.metadata?.rowCount;
            return rowCount !== undefined
              ? `${rowCount.toLocaleString()} rows`
              : "-";
          }}
        />
        <Column field="version" header="Version" sortable />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "150px" }}
        />
      </DataTable>
      {uploadDialog}
      <StyledDialog
        header={
          selectedFileForUpdate?.isCarveout
            ? "Update Carveout File"
            : "Update Base Rate File"
        }
        visible={updateDialogVisible}
        onHide={() => {
          if (!isUploading) {
            setUpdateDialogVisible(false);
            setDisplayName("");
            setSelectedFile(null);
            setSelectedFileForUpdate(null);
            setUpdateTargetTermId(null);
            setUpdateMode("same");
            setUpdateActivateContracts(false);
            setValidationErrors([]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        }}
        style={{ width: "650px" }}
        closable={!isUploading}
        closeOnEscape={!isUploading}
      >
        <div className="p-fluid">
          {/* File Information Header */}
          <div className="mb-4 p-3 surface-100 border-round">
            <h6 className="mt-0 mb-2 text-900">
              {selectedFileForUpdate?.displayName}
            </h6>
            <div className="flex align-items-center gap-3 text-600">
              <span>
                <i className="pi pi-file mr-1" />
                {selectedFileForUpdate?.fileName}
              </span>
              <span>
                <i className="pi pi-tag mr-1" />
                Version {updateContextRow?.version}
              </span>
            </div>
            {selectedFileForUpdate?.id && (
              <div className="text-xs text-500 font-mono mt-2">
                Row id (ops): {selectedFileForUpdate.id}
              </div>
            )}
          </div>

          <div className="field">
            <label className="font-medium">Contracts using this file</label>
            <div className="p-3 surface-50 border-1 border-200 border-round text-sm max-h-12rem overflow-y-auto">
              {contractsScanLoading || termsLoading ? (
                <span className="text-600">Loading contracts…</span>
              ) : null}
              {!contractsScanLoading &&
              !termsLoading &&
              (isCarveoutUpdateFile
                ? contractsUsingThisCarveout
                : contractsUsingThisBaseRate
              ).length === 0 ? (
                <span className="text-600">
                  {isCarveoutUpdateFile
                    ? "No contracts attach this carveout file (any term)."
                    : "No contracts reference this base rate file (any term)."}
                </span>
              ) : null}
              {!contractsScanLoading &&
              !termsLoading &&
              (isCarveoutUpdateFile
                ? contractsUsingThisCarveout
                : contractsUsingThisBaseRate
              ).length > 0 ? (
                <ul className="list-none m-0 p-0">
                  {(isCarveoutUpdateFile
                    ? contractsUsingThisCarveout
                    : contractsUsingThisBaseRate
                  ).map((c) => {
                    // eslint-disable-next-line no-underscore-dangle
                    const termsInGroupNav = c._termsInGroup;
                    return (
                      <li key={c.id} className="mb-2">
                        <Link
                          to={contractDetailHref(c.id)}
                          state={{
                            termsInGroup: termsInGroupNav as
                              | IContractTermInGroup[]
                              | undefined,
                            contractGroupId: c.contractGroupId,
                          }}
                          className="no-underline"
                        >
                          <div className="font-medium text-primary">
                            {c.provider?.name ?? "—"} → {c.payer?.name ?? "—"}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="field">
            <span className="font-medium block mb-2">Upload type</span>
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="modeSame"
                  name="updateMode"
                  value="same"
                  onChange={() => handleUpdateModeChange("same")}
                  checked={updateMode === "same"}
                />
                <label htmlFor="modeSame" className="m-0 cursor-pointer">
                  Update existing term (new version, same effective date)
                </label>
              </div>
              {!isCarveoutUpdateFile && (
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="modeNewTerm"
                    name="updateMode"
                    value="newTerm"
                    onChange={() => handleUpdateModeChange("newTerm")}
                    checked={updateMode === "newTerm"}
                  />
                  <label htmlFor="modeNewTerm" className="m-0 cursor-pointer">
                    Upload new term (new effective date)
                  </label>
                </div>
              )}
              {isCarveoutUpdateFile && (
                <p className="text-600 text-sm m-0">
                  Carveout files only support a version update on an existing
                  term, they do not create new contract terms.
                </p>
              )}
            </div>
          </div>

          <div className="field">
            <label className="font-medium">Terms for this file</label>
            <div className="p-3 surface-50 border-1 border-200 border-round text-sm max-h-10rem overflow-y-auto">
              {termsLoading && <span className="text-600">Loading terms…</span>}
              {!termsLoading && fileTerms.length === 0 && (
                <span className="text-600">No term history loaded.</span>
              )}
              {!termsLoading && fileTerms.length > 0 && (
                <ul className="list-none m-0 p-0">
                  {fileTermsSortedForPicker.map((t) => (
                    <li key={t.id} className="mb-2">
                      <span className="font-medium">
                        {formatBaseRateTermRowDate(t)}
                      </span>
                      {` — v${t.version} — ${t.displayName}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {updateMode === "same" && !termsLoading && fileTerms.length > 0 && (
            <div className="field">
              <label htmlFor="updateTargetTerm" className="font-medium">
                Term to update *
              </label>
              <Dropdown
                inputId="updateTargetTerm"
                value={updateTargetTermId}
                options={fileTermsSortedForPicker.map((t) => ({
                  label: `${formatBaseRateTermRowDate(t)} — v${t.version} — ${
                    t.displayName
                  }`,
                  value: t.id,
                }))}
                onChange={(e) => setUpdateTargetTermId(e.value as string)}
                className="w-full"
                placeholder="Select term"
              />
              <small className="text-600">
                Choose which effective-dated term this CSV replaces; the list
                above is every term for this file name.
              </small>
            </div>
          )}

          {updateMode === "newTerm" && (
            <div className="field">
              <label htmlFor="newTermEffectiveDate" className="font-medium">
                New term effective date *
              </label>
              <Calendar
                id="newTermEffectiveDate"
                value={newTermEffectiveDate}
                onChange={(e) =>
                  setNewTermEffectiveDate(
                    calendarSelectionToLocalDay(e.value as Date | null)
                  )
                }
                showIcon
                className="w-full"
                dateFormat="mm-dd-yy"
              />
              <small className="text-600">
                This same date is used as the contract term effective date when
                you roll forward or create draft terms.
              </small>
              {newTermEffectiveDateClash && (
                <Message
                  severity="error"
                  className="w-full mt-2"
                  text="This effective date is already used by a base rate term for this file or by a contract that references this file. Choose a different day."
                />
              )}
            </div>
          )}

          {/* Display Name Field */}
          <div className="field">
            <label htmlFor="updateDisplayName" className="font-medium">
              Display Name *
            </label>
            <InputText
              id="updateDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a display name for this base rate file"
              className="w-full"
            />
            {updateDisplayNameConflict && (
              <Message
                severity="error"
                className="w-full mt-2"
                text={`Display name must be unique per account among logical files. Already used by “${updateDisplayNameConflict.displayName}” (${updateDisplayNameConflict.fileName}).`}
              />
            )}
          </div>

          {/* Version Information — same term only */}
          {updateMode === "same" && (
            <div className="field">
              <div className="p-3 surface-50 border-1 border-200 border-round">
                <div className="flex align-items-center justify-content-between mb-2">
                  <span className="font-medium text-900">Version Update</span>
                  <span className="text-600">
                    {updateContextRow?.version} →{" "}
                    {(updateContextRow?.version || 0) + 1}
                  </span>
                </div>
                <p className="text-sm text-600 m-0">
                  Replaces the selected term’s file with a new version (same
                  effective date). Contracts that reference this term may be
                  marked outdated; no draft contract terms are created from this
                  action.
                </p>
              </div>
            </div>
          )}

          {!updateRolloutScanReady && (
            <p className="text-600 text-sm">
              Checking contract usage for this file…
            </p>
          )}
          {updateMode === "newTerm" &&
            updateRolloutScanReady &&
            !isBaseRateFileUsedInContracts && (
              <p className="text-600 text-sm line-height-3 p-3 surface-50 border-round border-1 border-200">
                No contracts currently reference this base rate file (any term).
                Roll forward and draft options below appear after at least one
                contract rate line uses it.
              </p>
            )}
          {showRolloutDraftOptions && (
            <>
              <div className="field flex align-items-center gap-2">
                <Checkbox
                  inputId="updateActivateContracts"
                  checked={updateActivateContracts}
                  onChange={(e) => setUpdateActivateContracts(!!e.checked)}
                />
                <label
                  htmlFor="updateActivateContracts"
                  className="m-0 cursor-pointer"
                >
                  Roll forward active contracts that use older terms of this
                  logical file
                </label>
              </div>
              <p className="text-600 text-sm line-height-3 m-0 mb-2">
                Unchecked: the server should create <strong>Draft</strong>{" "}
                contract term(s) for affected provider/payer deals using this
                logical file—no provider procedures until you activate those
                drafts. Checked: roll active terms forward (per server rules).
                The new term date above is used as the contract effective date.
              </p>
            </>
          )}

          {/* File Upload Section */}
          <div className="field">
            <label className="font-medium">New Base Rate File (CSV) *</label>

            {!selectedFile ? (
              <div
                className="p-4 border-2 border-dashed border-300 border-round text-center cursor-pointer hover:border-400 transition-colors"
                onClick={() =>
                  document.getElementById("file-update-input")?.click()
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById("file-update-input")?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Click to select a new CSV file"
              >
                <i className="pi pi-cloud-upload text-4xl text-400 mb-3" />
                <p className="text-600 mb-2">Click to select a new CSV file</p>
                <p className="text-sm text-500">or drag and drop here</p>
              </div>
            ) : (
              <div className="selected-files-list">
                <div className="selected-file-item bg-primary-50 border-primary-200">
                  <i className="pi pi-file text-primary mr-2" />
                  <span className="file-name text-primary font-medium flex-1">
                    {selectedFile.name}
                  </span>
                  <span className="text-sm text-600 mr-2">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => setSelectedFile(null)}
                  />
                </div>
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                  setValidationErrors([]);
                  e.target.value = "";
                }
              }}
              style={{ display: "none" }}
              id="file-update-input"
            />
          </div>

          {/* Warning Message */}
          <div className="p-3 border-left-3 border-orange-500 surface-ground">
            <div className="flex align-items-start">
              <i className="pi pi-exclamation-triangle text-orange-500 mr-2 mt-1" />
              <div>
                <h6 className="mt-0 mb-2 text-900">Important</h6>
                <p className="text-sm text-700 m-0 line-height-3">
                  {updateMode === "same" &&
                    "Replacing this term’s file does not create draft contract terms. Active contracts that use these rates may show as outdated until you refresh procedures."}
                  {updateMode === "newTerm" &&
                    !showRolloutDraftOptions &&
                    "This file is not referenced by any contract rate lines yet, so no contract rollout or draft creation runs from this new-term update."}
                  {showRolloutDraftOptions &&
                    "With roll forward off, the API should create Draft contract term(s). Contract effective date matches the new base rate term date. Provider procedures are not generated for Draft until you activate."}
                </p>
              </div>
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div className="mt-3 p-3 border-round surface-100 border-1 border-red-300">
              <div className="flex align-items-center gap-2 mb-2 text-red-700 font-semibold">
                <i className="pi pi-exclamation-triangle" />
                File validation failed
              </div>
              <ul className="m-0 pl-4 text-sm line-height-3">
                {validationErrors.map((err, i) => (
                  <li key={i} className="mb-1 text-red-700">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setUpdateDialogVisible(false);
                setSelectedFile(null);
                setSelectedFileForUpdate(null);
                setUpdateTargetTermId(null);
                setDisplayName("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              disabled={isUploading}
            />
            <Button
              label={isUploading ? "Updating..." : "Update File"}
              icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
              onClick={handleUpdate}
              disabled={
                isUploading ||
                !selectedFile ||
                !displayName.trim() ||
                !!updateDisplayNameConflict ||
                newTermEffectiveDateClash ||
                (updateMode === "same" &&
                  fileTerms.length > 0 &&
                  !updateTargetTermId)
              }
              loading={isUploading}
            />
          </div>
        </div>
      </StyledDialog>
      <CreateBaseRateFileDialog
        visible={createDialogVisible}
        onHide={() => setCreateDialogVisible(false)}
        sourceDataStatus={sourceStatusData?.data ?? null}
        existingBaseRateFiles={baseRateFiles}
      />
      <UploadSourceFilesDialog
        visible={uploadSourceFilesDialogVisible}
        onHide={() => setUploadSourceFilesDialogVisible(false)}
        sourceDataStatus={sourceStatusData?.data ?? null}
        onSuccess={() => {
          refetchSourceStatus();
        }}
      />
    </div>
  );
};

export default BaseRateFilesTable;
